import axios from "axios";
const constants = require("../configs/constants");

// Initialize axios instance for PostgREST with strict anti-cache headers
const pgrstClient = axios.create({
  baseURL: constants.HOST_POSTGREST,
  headers: {
    "Content-Type": "application/json",
    "Cache-Control": "no-cache, no-store, must-revalidate",
    Pragma: "no-cache",
    Expires: "0",
  },
});

// Enforce anti-cache headers on every request to prevent browser/WebView heuristic caching
pgrstClient.interceptors.request.use((config) => {
  config.headers["Cache-Control"] = "no-cache, no-store, must-revalidate";
  config.headers["Pragma"] = "no-cache";
  config.headers["Expires"] = "0";
  return config;
});

// Cache for token and user id
let currentAuth = null;
let authPromise = null;

// Try to recover token from sessionStorage on startup
try {
  const savedToken = typeof sessionStorage !== "undefined" ? sessionStorage.getItem("pgrst_token") : null;
  const savedUserId = typeof sessionStorage !== "undefined" ? sessionStorage.getItem("pgrst_user_id") : null;
  const savedLineId = typeof sessionStorage !== "undefined" ? sessionStorage.getItem("pgrst_line_id") : null;
  if (savedToken && savedUserId) {
    currentAuth = {
      token: savedToken,
      user_id: parseInt(savedUserId, 10),
      line_user_id: savedLineId || "",
    };
    pgrstClient.defaults.headers.common["Authorization"] = `Bearer ${savedToken}`;
  }
} catch (e) {}

/**
 * Exchange line_user_id for a signed PostgREST JWT (role: api_user)
 * Calls POST /rpc/auth_citizen
 */
export const authCitizen = async (line_user_id) => {
  if (!line_user_id) return null;

  try {
    const response = await pgrstClient.post("/rpc/auth_citizen", {
      line_user_id: line_user_id,
    });

    const authData = response.data?.[0];
    if (authData?.token) {
      currentAuth = {
        token: authData.token,
        user_id: authData.user_id,
        line_user_id: line_user_id,
      };

      pgrstClient.defaults.headers.common["Authorization"] = `Bearer ${authData.token}`;

      try {
        if (typeof sessionStorage !== "undefined") {
          sessionStorage.setItem("pgrst_token", authData.token);
          sessionStorage.setItem("pgrst_user_id", String(authData.user_id));
          sessionStorage.setItem("pgrst_line_id", line_user_id);
        }
      } catch (e) {}

      return currentAuth;
    }
  } catch (error) {
    console.error("❌ Failed to authenticate citizen with PostgREST:", error);
    throw error;
  }
  return null;
};

/**
 * Ensures valid PostgREST authentication before executing private queries/mutations
 */
export const ensureAuth = async (line_user_id) => {
  if (currentAuth?.token && (!line_user_id || currentAuth.line_user_id === line_user_id)) {
    return currentAuth;
  }
  if (!line_user_id) return currentAuth;

  if (authPromise) return authPromise;

  authPromise = authCitizen(line_user_id).finally(() => {
    authPromise = null;
  });

  return authPromise;
};

/**
 * Get news types (Protected: requires api_user token)
 */
export const getListOption = async (line_user_id) => {
  await ensureAuth(line_user_id);
  const response = await pgrstClient.get("/news_types", {
    params: {
      order: "order.asc",
      select: "id,name,order",
    },
  });

  return {
    fondue_alert_news_type: response.data,
  };
};

/**
 * Get Traffy User ID from line_user_id
 */
export const getTraffyId = async (line_user_id) => {
  const auth = await ensureAuth(line_user_id);
  if (auth?.user_id) {
    return {
      traffy_user: [{ id: auth.user_id }],
    };
  }

  const response = await pgrstClient.post("/rpc/get_traffy_user_id", {
    line_user_id: line_user_id,
  });

  return {
    traffy_user: response.data,
  };
};

/**
 * Find area by province, district, sub_district (Protected: requires api_user token)
 */
export const getAreaID = async (province, district, sub_district) => {
  await ensureAuth();
  const response = await pgrstClient.get("/areas", {
    params: {
      district: `eq.${district}`,
      province: `eq.${province}`,
      sub_district: `eq.${sub_district}`,
      select: "id",
    },
  });

  return {
    result: response.data,
  };
};

/**
 * Create or save an area (Protected: requires api_user token)
 */
export const saveArea = async (province, district, sub_district) => {
  await ensureAuth();
  const response = await pgrstClient.post(
    "/areas",
    {
      province: province,
      district: district,
      sub_district: sub_district,
    },
    {
      params: {
        on_conflict: "sub_district,district,province",
      },
      headers: {
        Prefer: "resolution=merge-duplicates, return=representation",
      },
    }
  );

  return {
    insert_fondue_alert_areas: {
      returning: response.data,
    },
  };
};

/**
 * Get citizen's subscribed areas and their selected news types (Protected by RLS)
 */
export const getListSubscription = async (line_user_id) => {
  const auth = await ensureAuth(line_user_id);
  let traffyUserId = auth?.user_id;

  if (!traffyUserId) {
    try {
      const userRes = await getTraffyId(line_user_id);
      traffyUserId = userRes.traffy_user?.[0]?.id;
    } catch (err) {
      console.error("Failed to get traffy user ID:", err);
    }
  }

  if (!traffyUserId) {
    return {
      traffy_user: [
        {
          username: line_user_id,
          traffy_user_user_subscription_areas: [],
        },
      ],
    };
  }

  const response = await pgrstClient.get("/user_subscription_areas", {
    params: {
      traffy_user_id: `eq.${traffyUserId}`,
      deleted_at: "is.null",
      order: "id.asc",
      select:
        "id,lat,lon,area_id,notification_status,name,areas(id,sub_district,district,province),user_subscription_news_type_in_areas(id,news_type_id,notification_status,news_types(order,name))",
    },
  });

  const mappedAreas = (response.data || []).map((area) => {
    const areaInfo = area.areas;
    const locationName = areaInfo
      ? `${areaInfo.sub_district || ""} ${areaInfo.district || ""} ${areaInfo.province || ""}`.trim()
      : area.name || "";

    return {
      id: area.id,
      lat: area.lat,
      lon: area.lon,
      area_id: area.area_id,
      name: area.name,
      location_name: locationName,
      areas: areaInfo,
      notification_status: area.notification_status,
      user_subscription_areas_user_subscription_news_type_in_areas: (
        area.user_subscription_news_type_in_areas || []
      ).map((item) => ({
        id: item.id,
        news_type_id: item.news_type_id,
        notification_status: item.notification_status,
        user_subscription_news_type_in_areas_news_type: item.news_types || {
          name: "",
          order: 0,
        },
      })),
    };
  });

  return {
    traffy_user: [
      {
        username: line_user_id,
        traffy_user_user_subscription_areas: mappedAreas,
      },
    ],
  };
};

/**
 * Save new citizen area subscription (Protected by RLS)
 */
export const saveAreaUser = async (area_id, lat, lon, traffy_user_id) => {
  const targetUserId = traffy_user_id || currentAuth?.user_id;
  const response = await pgrstClient.post(
    "/user_subscription_areas",
    {
      area_id: area_id,
      lat: `${lat}`,
      lon: `${lon}`,
      notification_status: true,
      traffy_user_id: targetUserId,
    },
    {
      headers: {
        Prefer: "return=representation",
      },
    }
  );

  return {
    insert_fondue_alert_user_subscription_areas: {
      returning: response.data,
    },
  };
};

/**
 * Update citizen area subscription (Protected by RLS)
 */
export const updateAreaUser = async (
  user_subscription_areas_id,
  area_id,
  lat,
  lon,
  notification_status
) => {
  const payload = {
    updated_at: new Date().toISOString(),
  };
  if (area_id !== undefined && area_id !== null) payload.area_id = area_id;
  if (lat !== undefined && lat !== null) payload.lat = `${lat}`;
  if (lon !== undefined && lon !== null) payload.lon = `${lon}`;
  if (notification_status !== undefined) payload.notification_status = notification_status;

  const response = await pgrstClient.patch(
    "/user_subscription_areas",
    payload,
    {
      params: {
        id: `eq.${user_subscription_areas_id}`,
      },
      headers: {
        Prefer: "return=representation",
      },
    }
  );

  return {
    update_fondue_alert_user_subscription_areas: {
      returning: response.data,
    },
  };
};

/**
 * Save multiple news type preferences in bulk (Protected by RLS)
 */
export const saveSubscriptionUserBatch = async (items) => {
  if (!items || items.length === 0) {
    return {
      insert_fondue_alert_user_subscription_news_type_in_areas: {
        returning: [],
      },
    };
  }

  const response = await pgrstClient.post(
    "/user_subscription_news_type_in_areas",
    items,
    {
      params: {
        on_conflict: "user_subscription_area_id,news_type_id",
      },
      headers: {
        Prefer: "resolution=merge-duplicates, return=representation",
      },
    }
  );

  return {
    insert_fondue_alert_user_subscription_news_type_in_areas: {
      returning: response.data,
    },
  };
};

/**
 * Save news type preference for an area (Protected by RLS)
 */
export const saveSubscriptionUser = async (
  news_type_id,
  user_subscription_area_id,
  notification_status
) => {
  if (Array.isArray(news_type_id)) {
    return saveSubscriptionUserBatch(news_type_id);
  }

  const response = await pgrstClient.post(
    "/user_subscription_news_type_in_areas",
    {
      news_type_id: news_type_id,
      user_subscription_area_id: user_subscription_area_id,
      notification_status: notification_status,
    },
    {
      params: {
        on_conflict: "user_subscription_area_id,news_type_id",
      },
      headers: {
        Prefer: "resolution=merge-duplicates, return=representation",
      },
    }
  );

  return {
    insert_fondue_alert_user_subscription_news_type_in_areas: {
      returning: response.data,
    },
  };
};

/**
 * Delete (soft-delete) area subscription (Protected by RLS)
 */
export const deleteSubscription = async (user_subscription_area_id) => {
  const now = new Date().toISOString();
  const response = await pgrstClient.patch(
    "/user_subscription_areas",
    {
      updated_at: now,
      deleted_at: now,
    },
    {
      params: {
        id: `eq.${user_subscription_area_id}`,
      },
      headers: {
        Prefer: "return=representation",
      },
    }
  );

  return {
    update_fondue_alert_user_subscription_areas: {
      returning: response.data,
    },
  };
};

/**
 * Get all notifications for citizen (Protected by RLS)
 */
export const getAllNews = async (
  line_user_id,
  { limit = 50, offset = 0 } = {}
) => {
  const auth = await ensureAuth(line_user_id);
  let traffyUserId = auth?.user_id;

  if (!traffyUserId) {
    try {
      const userRes = await getTraffyId(line_user_id);
      traffyUserId = userRes.traffy_user?.[0]?.id;
    } catch (err) {
      console.error("Failed to get traffy user ID:", err);
    }
  }

  if (!traffyUserId) {
    return {
      traffy_user: [
        {
          traffy_user_id: null,
          line_user_id: line_user_id,
          fondue_alert_notifications: [],
        },
      ],
    };
  }

  const params = {
    traffy_user_id: `eq.${traffyUserId}`,
    deleted_at: "is.null",
    order: "created_at.desc",
    select:
      "id,read,created_at,news(id,title,desription,url,photo,lat,lon,radius_km,created_at,news_types(id,name),voice_fonduegroup(id,name),news_alerts_in_areas(areas(id,sub_district,district,province)))",
  };
  if (limit !== undefined && limit !== null) params.limit = limit;
  if (offset !== undefined && offset !== null) params.offset = offset;

  const response = await pgrstClient.get("/user_notifications", { params });

  const mappedNotifications = (response.data || [])
    .filter((item) => item.news != null)
    .map((item) => ({
      notification_id: item.id,
      read: item.read,
      news: {
        ...(item.news || {}),
        news_type: item.news?.news_types || { name: "" },
        voice_fonduegroup: item.news?.voice_fonduegroup || { name: "" },
        news_alerts_in_areas: (item.news?.news_alerts_in_areas || []).map(
          (alert) => ({
            area: alert.areas || {},
          })
        ),
      },
    }));

  return {
    traffy_user: [
      {
        traffy_user_id: traffyUserId,
        line_user_id: line_user_id,
        fondue_alert_notifications: mappedNotifications,
      },
    ],
  };
};

/**
 * Mark notification as read (Protected by RLS)
 */
export const updateNews = async (traffy_user_id, news_id) => {
  const targetUserId = traffy_user_id || currentAuth?.user_id;
  const response = await pgrstClient.post(
    "/user_notifications",
    {
      traffy_user_id: targetUserId,
      news_id: news_id,
      read: true,
      updated_at: new Date().toISOString(),
    },
    {
      params: {
        on_conflict: "traffy_user_id,news_id",
      },
      headers: {
        Prefer: "resolution=merge-duplicates, return=representation",
      },
    }
  );

  return {
    insert_fondue_alert_notifications: {
      returning: response.data,
    },
  };
};

/**
 * Get published news catalog with pagination (Protected: requires api_user token)
 */
export const getAllOfNews = async (
  line_user_id,
  { limit = 50, offset = 0 } = {}
) => {
  await ensureAuth(line_user_id);
  const params = {
    order: "created_at.desc",
    select:
      "id,title,desription,url,photo,lat,lon,radius_km,created_at,news_types(id,name),voice_fonduegroup(id,name),news_alerts_in_areas(areas(id,province,district,sub_district))",
  };
  if (limit !== undefined && limit !== null) params.limit = limit;
  if (offset !== undefined && offset !== null) params.offset = offset;

  const response = await pgrstClient.get("/news", { params });

  const mapped = (response.data || []).map((item) => ({
    ...item,
    news_news_type: item.news_types || { name: "" },
    news_voice_fonduegroup: item.voice_fonduegroup || { name: "" },
    news_alerts_in_areas: (item.news_alerts_in_areas || []).map((alert) => ({
      news_alerts_in_areas_area: alert.areas || {},
    })),
  }));

  return {
    news: mapped,
  };
};
